/**
 * SeatBar — Indicatore visuale "X di Y posti occupati" per tenant.
 *
 * Visivo:
 *   ┌──────────────────────┐
 *   │ ██████░░░░░░░░░░░░░░ │  6/15
 *   └──────────────────────┘
 *
 * Colori:
 *  - default (≤ 70%):  grafite
 *  - warning  (≤ 95%): ambra
 *  - danger   (> 95%): rosso (vicino al cap)
 *  - exempt:           viola tenue (tenant interni, quota = ∞)
 *
 * Tipografia: contatore "6/15" in mono per allineamento perfetto in colonna
 * tabella.
 *
 * @since 2026-05-18
 */

import React from 'react';
import { Box, Tooltip } from '@mui/material';
import { tokens } from '../../theme';

interface SeatBarProps {
  used: number | undefined;
  quota: number | undefined;
  /** Tenant interno (billing.exempt=true): mostra "∞" e barra al 100% viola tenue. */
  exempt?: boolean;
  /** Larghezza in px della barra. Default 80. */
  width?: number;
  /** Se true mostra solo i numeri (uso compatto in cell strette). */
  textOnly?: boolean;
}

interface VisualResolution {
  pct: number;
  fillColor: string;
  trackColor: string;
  displayText: string;
  tooltip: string;
}

function resolveVisual(used: number, quota: number, exempt: boolean): VisualResolution {
  if (exempt) {
    return {
      pct: 100,
      fillColor: tokens.color.tierCustom.main,
      trackColor: tokens.color.tierCustom.dim,
      displayText: `${used}/∞`,
      tooltip: `${used} utenti attivi · seat illimitati (tenant interno)`,
    };
  }
  if (!quota || quota <= 0) {
    return {
      pct: 0,
      fillColor: tokens.color.mist,
      trackColor: tokens.color.lineSoft,
      displayText: `${used}/—`,
      tooltip: 'Nessuna quota configurata',
    };
  }
  const pct = Math.min(100, Math.round((used / quota) * 100));
  let fillColor: string = tokens.color.graphite;
  let tooltipExtra = '';
  if (pct > 95) {
    fillColor = tokens.color.danger;
    tooltipExtra = ' · al limite';
  } else if (pct > 70) {
    fillColor = tokens.color.warning;
    tooltipExtra = ' · vicino al limite';
  }
  return {
    pct,
    fillColor,
    trackColor: tokens.color.line,
    displayText: `${used}/${quota}`,
    tooltip: `${used} utenti attivi su ${quota} seat (${pct}%)${tooltipExtra}`,
  };
}

export const SeatBar: React.FC<SeatBarProps> = ({
  used = 0,
  quota,
  exempt = false,
  width = 80,
  textOnly = false,
}) => {
  const vis = resolveVisual(used, quota ?? 0, exempt);

  const counter = (
    <Box
      component="span"
      sx={{
        fontFamily: tokens.font.mono,
        fontSize: '0.74rem',
        fontWeight: 600,
        color: tokens.color.graphite,
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap',
      }}
    >
      {vis.displayText}
    </Box>
  );

  if (textOnly) {
    return <Tooltip title={vis.tooltip} arrow><span>{counter}</span></Tooltip>;
  }

  return (
    <Tooltip title={vis.tooltip} arrow>
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
        <Box
          sx={{
            width,
            height: 6,
            borderRadius: 3,
            bgcolor: vis.trackColor,
            position: 'relative',
            overflow: 'hidden',
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: `${vis.pct}%`,
              bgcolor: vis.fillColor,
              borderRadius: 3,
              transition: 'width 240ms ease-out',
            }}
          />
        </Box>
        {counter}
      </Box>
    </Tooltip>
  );
};

export default SeatBar;
